import { useEffect, useState, useCallback, useRef, memo, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DollarSign, TrendingDown, TrendingUp, Info, RefreshCw, Search } from 'lucide-react';

// Safely extract a renderable string from any error shape.
// FastAPI 422 returns `detail` as an array of {type, loc, msg, input, ctx, url}
// dicts — passing that to toast/JSX directly crashes React with
// "Objects are not valid as a React child".
const errMsg = (e, fallback = 'Save failed') => {
  const detail = e?.response?.data?.detail ?? e?.message ?? null;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => (d && typeof d === 'object' ? (d.msg || d.detail || JSON.stringify(d)) : String(d))).join(' · ') || fallback;
  }
  if (typeof detail === 'object') return detail.msg || detail.detail || JSON.stringify(detail);
  return String(detail);
};

const fmtINR = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `₹${Math.round(Number(v)).toLocaleString('en-IN')}`;
};
const fmtPct1 = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const r = Math.round(Number(v) * 10) / 10;
  return `${Number.isInteger(r) ? r.toFixed(0) : r.toFixed(1)}%`;
};

const recompute = (cost, tgt, swg, zmt, menu) => {
  const t = Number(tgt) || 0;
  const s = Number(swg) || 0;
  const z = Number(zmt) || 0;
  const c = Number(cost) || 0;
  const suggested = t > 0 ? (c * 100) / t : 0;
  const menuN = menu === '' || menu === null || menu === undefined ? null : Number(menu);
  const base = menuN && menuN > 0 ? menuN : suggested;
  const swiggy = s < 100 ? base / (1 - s / 100) : 0;
  const zomato = z < 100 ? base / (1 - z / 100) : 0;
  const actual = base > 0 ? (c * 100) / base : 0;
  return {
    suggested_price: Math.round(suggested * 100) / 100,
    base_price: Math.round(base * 100) / 100,
    swiggy_price: Math.round(swiggy * 100) / 100,
    zomato_price: Math.round(zomato * 100) / 100,
    actual_food_cost_pct: Math.round(actual * 100) / 100,
  };
};

const fcClass = (actual, target) => {
  if (!actual || actual <= 0) return 'text-[#5C6B64] bg-[#F9F9F8]';
  if (actual <= target) return 'text-emerald-800 bg-emerald-50';
  if (actual <= target + 3) return 'text-amber-800 bg-amber-50';
  return 'text-rose-800 bg-rose-50';
};

const PricingRow = memo(function PricingRow({ row, onChange }) {
  const overTarget = row.actual_food_cost_pct > row.target_food_cost_pct;
  const isBev = (row.category_type || 'food') === 'beverage';
  return (
    <tr
      className="border-b border-[#E2E8DF]/70 hover:bg-[#F9F9F8] odd:bg-white even:bg-[#FBFBFA]"
      data-testid={`pricing-row-${row.id}`}
    >
      <td className="px-3 py-2.5 sticky left-0 z-[1] bg-inherit" style={{ width: 220 }}>
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${isBev ? 'text-blue-700 bg-blue-50 border-blue-200' : 'text-emerald-700 bg-emerald-50 border-emerald-200'}`}
            title={isBev ? 'Beverage' : 'Food'}
          >
            {isBev ? 'Bev' : 'Food'}
          </span>
          <span className="font-semibold text-[#1A2421] truncate" title={row.name}>{row.name}</span>
        </div>
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap" style={{ width: 120 }}>
        <span className="font-bold text-[#1A2421]">{fmtINR(row.cost_per_unit)}</span>
        <span className="text-[9px] uppercase tracking-wider text-[#5C6B64] ml-1">/{row.unit_label || 'portion'}</span>
      </td>
      <td className="px-2 py-2.5 text-center whitespace-nowrap" style={{ width: 100 }}>
        <Input
          type="number" step="0.1" min="1" max="100"
          value={row.target_food_cost_pct ?? ''}
          onChange={(e) => onChange(row.id, 'target_food_cost_pct', Number(e.target.value))}
          className="h-7 w-[68px] text-right tabular-nums text-xs px-1.5 mx-auto"
          data-testid={`pricing-target-${row.id}`}
        />
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-bold text-[#5C6B64]" style={{ width: 140 }}>
        {fmtINR(row.suggested_price)}
      </td>
      <td className="px-2 py-2.5 text-center whitespace-nowrap" style={{ width: 140 }}>
        <Input
          type="number" step="1" min="0"
          placeholder={row.suggested_price ? String(Math.round(row.suggested_price)) : '—'}
          value={row.menu_price ?? ''}
          onChange={(e) => onChange(row.id, 'menu_price', e.target.value === '' ? null : Number(e.target.value))}
          className="h-7 w-[96px] text-right tabular-nums text-xs font-bold px-1.5 mx-auto"
          data-testid={`pricing-menu-${row.id}`}
        />
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-bold text-[#0F7A4F]" style={{ width: 140 }}>
        {fmtINR(row.swiggy_price)}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-bold text-[#0F7A4F]" style={{ width: 140 }}>
        {fmtINR(row.zomato_price)}
      </td>
      <td className="px-2 py-2.5 text-right whitespace-nowrap" style={{ width: 120 }}>
        <span
          className={`inline-flex items-center justify-end gap-1 px-2 py-0.5 rounded tabular-nums font-bold text-xs ${fcClass(row.actual_food_cost_pct, row.target_food_cost_pct)}`}
          data-testid={`pricing-actual-${row.id}`}
        >
          {overTarget ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {fmtPct1(row.actual_food_cost_pct ?? 0)}
        </span>
      </td>
    </tr>
  );
});

export default function PricingTab({ isActive = true }) {
  const { getHeaders, API } = useAuth();
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState({
    target_food_cost_pct: 30,
    swiggy_commission: 22,
    zomato_commission: 25,
  });
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const saveTimers = useRef({});
  const commissionTimer = useRef(null);
  const itemsRef = useRef(items);
  const settingsRef = useRef(settings);
  itemsRef.current = items;
  settingsRef.current = settings;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pricing`, { headers: getHeaders() });
      setItems(r.data.items || []);
      setSettings((prev) => r.data.settings || prev);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to load pricing'));
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders]);

  useEffect(() => { if (isActive) load(); /* eslint-disable-next-line */ }, [isActive]);

  const updateRow = useCallback((id, field, value) => {
    setItems((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      const next = { ...r, [field]: value };
      const math = recompute(
        next.cost_per_unit ?? next.cost_per_portion,
        next.target_food_cost_pct,
        settings.swiggy_commission,
        settings.zomato_commission,
        next.menu_price,
      );
      return { ...next, ...math };
    }));
    clearTimeout(saveTimers.current[id]);
    saveTimers.current[id] = setTimeout(async () => {
      const latest = (itemsRef.current || []).find((r) => r.id === id);
      if (!latest) return;
      // Client-side guard: never POST values that the backend will 422 on.
      // (Backend requires target_food_cost_pct > 0.) Sending an out-of-range
      // value used to surface as a validation-error array, which sonner then
      // tried to render as a child → "Objects are not valid as a React child".
      const body = {};
      const tfc = Number(latest.target_food_cost_pct);
      if (Number.isFinite(tfc) && tfc > 0 && tfc <= 100) {
        body.target_food_cost_pct = tfc;
      }
      if (latest.menu_price !== null && latest.menu_price !== undefined && latest.menu_price !== '') {
        const mp = Number(latest.menu_price);
        if (Number.isFinite(mp) && mp >= 0) body.menu_price = mp;
      }
      if (Object.keys(body).length === 0) return;  // nothing valid to save yet
      try {
        await axios.put(`${API}/recipes/${id}/pricing`, body, { headers: getHeaders() });
      } catch (e) {
        toast.error(errMsg(e, 'Save failed'));
      }
    }, 700);
  }, [API, getHeaders, settings.swiggy_commission, settings.zomato_commission]);

  const updateCommission = useCallback((field, value) => {
    // Field is 'swiggy_commission' | 'zomato_commission'. Update tenant-level
    // settings, recompute every row's platform prices live (no fetch round
    // trip), then debounce-save the settings block to the backend.
    setSettings((prev) => {
      const nextSettings = { ...prev, [field]: value };
      setItems((rows) => rows.map((r) => {
        const math = recompute(
          r.cost_per_unit ?? r.cost_per_portion,
          r.target_food_cost_pct,
          nextSettings.swiggy_commission,
          nextSettings.zomato_commission,
          r.menu_price,
        );
        return { ...r, ...math };
      }));
      return nextSettings;
    });
    clearTimeout(commissionTimer.current);
    commissionTimer.current = setTimeout(async () => {
      // Read the latest committed settings from the ref (not closure) so a
      // rapid Swiggy → Zomato edit sequence persists both values correctly.
      const cur = settingsRef.current;
      const body = {};
      const swg = Number(cur.swiggy_commission);
      const zmt = Number(cur.zomato_commission);
      const tfc = Number(cur.target_food_cost_pct);
      if (Number.isFinite(swg) && swg >= 0 && swg < 100) body.swiggy_commission = swg;
      if (Number.isFinite(zmt) && zmt >= 0 && zmt < 100) body.zomato_commission = zmt;
      // Round-trip the existing tenant target FC % so the backend's
      // PricingSettings model (all-fields) doesn't overwrite the stored value
      // with its default. Tenant-level Theo % is still set elsewhere.
      if (Number.isFinite(tfc) && tfc > 0 && tfc <= 100) body.target_food_cost_pct = tfc;
      if (Object.keys(body).length === 0) return;
      try {
        await axios.put(`${API}/pricing/settings`, body, { headers: getHeaders() });
      } catch (e) {
        toast.error(errMsg(e, 'Failed to save commissions'));
      }
    }, 700);
  }, [API, getHeaders]);

  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((r) => categoryFilter === 'all' || (r.category_type || 'food') === categoryFilter)
      .filter((r) => !q || r.name.toLowerCase().includes(q));
  }, [items, categoryFilter, search]);

  return (
    <div className="w-full space-y-3 pb-24" data-testid="pricing-tab">
      {/* Pricing table — recipe-level controls only.
          Theo % and Menu Price are edited inline per row. Platform commissions
          (Swiggy / Zomato) are tenant-level settings consumed silently from
          /api/pricing's `settings` block; they are intentionally NOT exposed
          here to keep this screen recipe-focused. */}
      <Card className="border-[#E2E8DF] shadow-sm">
        <CardContent className="p-0">
          {/* Toolbar: search + category filter + platform commissions
              ──────────────────────────────────────────────────────────────
              Platform commissions (Swiggy / Zomato) are tenant-level pricing
              assumptions — kept compact inline (no oversized header card),
              auto-saved on debounce. Theo % stays per-recipe inside the
              table rows. */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E2E8DF] flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-[320px]">
              <Search className="w-3.5 h-3.5 text-[#5C6B64] absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search dish name…"
                className="h-8 pl-8 text-xs"
                data-testid="pricing-search"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Platform commissions — tenant-level, auto-saved (debounced 700ms) */}
              <div className="inline-flex items-stretch gap-0 rounded-md border border-[#E2E8DF] bg-white overflow-hidden"
                data-testid="pricing-platform-commissions">
                <span className="px-2 flex items-center text-[9px] uppercase tracking-wider text-[#5C6B64] font-semibold border-r border-[#E2E8DF] bg-[#F9F9F8]">Platform</span>
                <div className="flex items-center gap-1 px-2 border-r border-[#E2E8DF]">
                  <span className="text-[10px] text-[#5C6B64] font-semibold">Swiggy</span>
                  <Input
                    type="number" step="0.5" min="0" max="99"
                    value={settings.swiggy_commission ?? 0}
                    onChange={(e) => updateCommission('swiggy_commission', e.target.value === '' ? 0 : Number(e.target.value))}
                    data-testid="pricing-swiggy-commission"
                    className="h-6 w-[52px] text-right tabular-nums text-xs px-1.5 border-0 focus-visible:ring-1"
                  />
                  <span className="text-[10px] text-[#5C6B64]">%</span>
                </div>
                <div className="flex items-center gap-1 px-2">
                  <span className="text-[10px] text-[#5C6B64] font-semibold">Zomato</span>
                  <Input
                    type="number" step="0.5" min="0" max="99"
                    value={settings.zomato_commission ?? 0}
                    onChange={(e) => updateCommission('zomato_commission', e.target.value === '' ? 0 : Number(e.target.value))}
                    data-testid="pricing-zomato-commission"
                    className="h-6 w-[52px] text-right tabular-nums text-xs px-1.5 border-0 focus-visible:ring-1"
                  />
                  <span className="text-[10px] text-[#5C6B64]">%</span>
                </div>
              </div>
              <div className="inline-flex items-center gap-1 p-0.5 rounded-md bg-[#F3F5F2] border border-[#E2E8DF]">
                {[{ k: 'all', label: 'All' }, { k: 'food', label: 'Food' }, { k: 'beverage', label: 'Beverage' }].map(o => (
                  <button key={o.k} type="button"
                    onClick={() => setCategoryFilter(o.k)}
                    data-testid={`pricing-cat-filter-${o.k}`}
                    className={`h-6 px-2.5 rounded text-[10px] font-semibold transition-colors ${categoryFilter === o.k ? 'bg-white shadow-sm text-[color:var(--color-primary)]' : 'text-[#5C6B64] hover:text-[#1A2421]'}`}>
                    {o.label}
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-[#5C6B64] tabular-nums">
                {loading ? 'Loading…' : `${visibleItems.length} of ${items.length}`}
              </div>
              <Button
                variant="outline" size="sm" onClick={load} disabled={loading}
                className="h-7 text-xs" data-testid="pricing-refresh"
              >
                <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="py-12 text-center">
              <DollarSign className="w-8 h-8 text-[#5C6B64]/30 mx-auto mb-2" />
              <p className="text-sm text-[#5C6B64]">
                {items.length === 0 ? 'No final recipes yet' : 'No matches for this search / filter'}
              </p>
              {items.length === 0 && (
                <p className="text-xs text-[#5C6B64] mt-1">Add recipes in the Recipes tab — they'll appear here automatically.</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[70vh]">
              <table className="text-sm" style={{ minWidth: 1000, width: '100%' }}>
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold bg-[#F9F9F8] border-y border-[#E2E8DF] sticky top-0 z-[2]">
                    <th className="px-3 py-2 text-left sticky left-0 bg-[#F9F9F8] z-[3]" style={{ width: 220 }}>Dish</th>
                    <th className="px-3 py-2 text-right" style={{ width: 120 }}>Cost / Unit</th>
                    <th className="px-2 py-2 text-center" style={{ width: 100 }}>Theo %</th>
                    <th className="px-3 py-2 text-right" style={{ width: 140 }}>Suggested</th>
                    <th className="px-2 py-2 text-center" style={{ width: 140 }}>Menu Price</th>
                    <th className="px-3 py-2 text-right" style={{ width: 140 }}>Swiggy</th>
                    <th className="px-3 py-2 text-right" style={{ width: 140 }}>Zomato</th>
                    <th className="px-2 py-2 text-right" style={{ width: 120 }}>Actual Theo %</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleItems.map((r) => (
                    <PricingRow key={r.id} row={r} onChange={updateRow} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="px-4 py-2.5 border-t border-[#E2E8DF] bg-[#F9F9F8] flex items-start gap-2 text-[11px] text-[#5C6B64]">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Platform price = Base / (1 − commission%). Empty Menu Price defaults to Suggested.
              Actual Theo %: <span className="text-emerald-800 font-semibold">green</span> within target ·{' '}
              <span className="text-amber-800 font-semibold">amber</span> +3% ·{' '}
              <span className="text-rose-800 font-semibold">red</span> over target.
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
